import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import DOMPurify from "dompurify";
import { useMemo } from "react";
import { FiClock, FiEdit2, FiTrash2 } from "react-icons/fi";
const timeAgo = (dateString) => {
    if (!dateString)
        return "";
    const date = new Date(dateString);
    const diffMs = Date.now() - date.getTime();
    const diffMinutes = Math.round(diffMs / (1000 * 60));
    if (diffMinutes < 1)
        return "À l'instant";
    if (diffMinutes < 60)
        return `Il y a ${diffMinutes} min`;
    const diffHours = Math.round(diffMinutes / 60);
    if (diffHours < 24)
        return `Il y a ${diffHours} h`;
    const diffDays = Math.round(diffHours / 24);
    if (diffDays < 7)
        return `Il y a ${diffDays} j`;
    return date.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};
const hasUserOwnership = (comment, currentUserId) => currentUserId !== undefined && comment.user_id === currentUserId;
const sanitizeContent = (content) => DOMPurify.sanitize(content, {
    ALLOWED_ATTR: ["href", "target", "rel", "class", "data-mention-id"],
    ALLOWED_TAGS: [
        "p",
        "strong",
        "em",
        "span",
        "code",
        "pre",
        "blockquote",
        "ul",
        "ol",
        "li",
        "a",
        "br",
        "hr",
    ],
});
const renderAttachment = (attachment) => {
    const downloadUrl = attachment.file_path
        ? attachment.file_path
        : attachment.id
            ? `/attachments/download/${attachment.id}`
            : undefined;
    return (_jsxs("li", { className: "flex items-center gap-2 text-sm", children: [_jsx("span", { className: "inline-flex h-6 w-6 items-center justify-center rounded bg-slate-100 text-slate-600", children: "\uD83D\uDCCE" }), downloadUrl ? (_jsx("a", { href: downloadUrl, className: "truncate text-blue-600 underline-offset-2 hover:underline", children: attachment.file_name })) : (_jsx("span", { className: "truncate text-slate-600", children: attachment.file_name }))] }, `${attachment.id}-${attachment.file_name}`));
};
const CommentItem = ({ comment, currentUserId, onEdit, onDelete, }) => {
    const sanitizedContent = useMemo(() => sanitizeContent(comment.content ?? ""), [comment.content]);
    return (_jsxs("article", { className: "group rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition hover:border-blue-200", role: "listitem", "aria-label": `Commentaire de ${comment.first_name ?? "Utilisateur"}`, children: [_jsxs("header", { className: "mb-2 flex items-start justify-between gap-4", children: [_jsxs("div", { children: [_jsxs("p", { className: "text-sm font-semibold text-slate-800", children: [comment.first_name, " ", comment.last_name] }), _jsxs("p", { className: "flex items-center gap-2 text-xs text-slate-500", children: [_jsx(FiClock, { "aria-hidden": "true" }), _jsx("span", { title: comment.created_at ?? undefined, children: timeAgo(comment.created_at) }), comment.updated_at && (_jsx("span", { className: "text-[11px] text-slate-400", children: "\u00B7 modifi\u00E9" }))] })] }), _jsxs("div", { className: "flex items-center gap-2 opacity-0 transition group-hover:opacity-100", children: [hasUserOwnership(comment, currentUserId) && (_jsx("button", { type: "button", onClick: () => onEdit(comment), className: "rounded border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600", children: _jsxs("span", { className: "flex items-center gap-1", children: [_jsx(FiEdit2, {}), " \u00C9diter"] }) })), hasUserOwnership(comment, currentUserId) && (_jsx("button", { type: "button", onClick: () => onDelete(comment), className: "rounded border border-slate-200 px-2 py-1 text-xs font-medium text-red-500 transition hover:border-red-200 hover:bg-red-50", children: _jsxs("span", { className: "flex items-center gap-1", children: [_jsx(FiTrash2, {}), " Supprimer"] }) }))] })] }), _jsx("div", { className: "prose prose-sm max-w-none text-slate-700", dangerouslySetInnerHTML: { __html: sanitizedContent } }), comment.attachments && comment.attachments.length > 0 && (_jsxs("div", { className: "mt-4 rounded border border-slate-200 bg-slate-50 p-3", children: [_jsx("p", { className: "mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500", children: "Pi\u00E8ces jointes" }), _jsx("ul", { className: "space-y-2", children: comment.attachments.map((attachment) => renderAttachment(attachment)) })] }))] }));
};
export default CommentItem;
